<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="me-2 mb-2">
            <select v-model="filters.frequency" class="form-select">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div class="me-2 mb-2" v-if="filters.frequency === 'monthly'">
            <input v-model="filters.period" type="month" class="form-control" />
          </div>
          <div class="me-2 mb-2" v-else>
            <input v-model="filters.period_start" type="date" class="form-control" />
          </div>
          <div class="me-2 mb-2" v-if="filters.frequency !== 'monthly'">
            <input v-model="filters.period_end" type="date" class="form-control" />
          </div>
          <div class="me-2 mb-2">
            <select v-model="filters.status" class="form-select">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="issued">Issued</option>
            </select>
          </div>
          <div class="me-2 mb-2">
            <button class="btn btn-primary" :disabled="loading" @click="fetchReport">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Load
            </button>
          </div>
          <div class="mb-2">
            <div class="dropdown">
              <a class="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item rounded-1" href="javascript:void(0);" @click.prevent="exportReport('csv')">
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a class="dropdown-item rounded-1" href="javascript:void(0);" @click.prevent="exportReport('pdf')">
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div v-if="summary" class="row dq-stat-grid">
        <div class="col-xl-3 col-md-6 d-flex mb-3" v-for="card in cards" :key="card.title">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2">
                <div>
                  <span class="fs-14 fw-normal text-truncate mb-1">{{ card.title }}</span>
                  <h5>{{ card.isMoney ? `${summary.currency} ${formatMoney(card.value)}` : card.value }}</h5>
                </div>
                <a href="javascript:void(0);" class="avatar avatar-md avatar-rounded" :class="`bg-transparent-${card.cls} border border-${card.cls}`">
                  <span :class="`text-${card.cls}`"><i :class="card.icon"></i></span>
                </a>
              </div>
              <p class="fs-12 fw-normal text-muted mb-0">Period: {{ summary.period_label || summary.period }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="summary" class="row dq-stat-grid">
        <div class="col-xl-3 col-md-6 d-flex mb-3" v-for="card in operatorCards" :key="card.title">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2">
                <div>
                  <span class="fs-14 fw-normal text-truncate mb-1">{{ card.title }}</span>
                  <h5>{{ card.isMoney ? `${summary.currency} ${formatMoney(card.value)}` : card.value }}</h5>
                </div>
                <a href="javascript:void(0);" class="avatar avatar-md avatar-rounded" :class="`bg-transparent-${card.cls} border border-${card.cls}`">
                  <span :class="`text-${card.cls}`"><i :class="card.icon"></i></span>
                </a>
              </div>
              <p class="fs-12 fw-normal text-muted mb-0">{{ card.note }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-info">
        Issue physical checks only after payroll is approved for the period.
        Rows marked <strong>Blocked</strong> unlock once approval completes on Employee Salary.
        Export the register PDF for filing after recording check numbers.
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Check Payroll Register</h5>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <a-table class="table datatable thead-light" :columns="columns" :data-source="rows" :loading="loading" :pagination="false" row-key="id">
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'amount'">
                  {{ record.currency }} {{ formatMoney(record.amount) }}
                </template>
                <template v-else-if="column.key === 'status'">
                  <span class="badge" :class="record.status === 'issued' ? 'badge-soft-success' : 'badge-soft-warning'">
                    {{ record.status === 'issued' ? 'Issued' : 'Pending' }}
                  </span>
                </template>
                <template v-else-if="column.key === 'approval_status'">
                  <span class="badge" :class="record.approval_status === 'approved' ? 'badge-soft-success' : 'badge-soft-danger'">
                    {{ record.approval_status === 'approved' ? 'Approved' : 'Blocked' }}
                  </span>
                </template>
                <template v-else-if="column.key === 'actions'">
                  <div class="d-flex gap-2 flex-wrap">
                    <button
                      v-if="record.status !== 'issued'"
                      class="btn btn-sm btn-success"
                      :disabled="record.approval_status !== 'approved' || actionLoadingId === record.id"
                      @click="openIssue(record)"
                    >
                      Issue Check
                    </button>
                    <button
                      v-else
                      class="btn btn-sm btn-outline-warning"
                      :disabled="actionLoadingId === record.id"
                      @click="markPending(record)"
                    >
                      <span v-if="actionLoadingId === record.id" class="spinner-border spinner-border-sm me-1"></span>
                      Revert
                    </button>
                  </div>
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="issueModalOpen" class="dq-modal-backdrop" @click.self="closeIssue">
      <div class="dq-modal-card">
        <div class="dq-modal-head">
          <div>
            <h5 class="mb-1">Issue Check</h5>
            <p class="mb-0 text-muted">{{ selectedRow?.employee_name }} • {{ selectedRow?.employee_id }}</p>
          </div>
          <button class="btn btn-icon btn-sm btn-light" @click="closeIssue">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="dq-modal-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Check Number <span class="text-danger">*</span></label>
              <input v-model.trim="issueForm.check_number" type="text" class="form-control" placeholder="From checkbook" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Check Date</label>
              <input v-model="issueForm.check_date" type="date" class="form-control" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Pickup / Receiver Name</label>
              <input v-model.trim="issueForm.receiver_name" type="text" class="form-control" placeholder="Optional" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Receiver ID / Document No</label>
              <input v-model.trim="issueForm.receiver_document_no" type="text" class="form-control" placeholder="Optional" />
            </div>
            <div class="col-12">
              <label class="form-label">Notes</label>
              <textarea v-model.trim="issueForm.notes" rows="3" class="form-control" placeholder="Void/reissue notes"></textarea>
            </div>
          </div>
        </div>
        <div class="dq-modal-foot">
          <button class="btn btn-light me-2" @click="closeIssue">Cancel</button>
          <button class="btn btn-success" :disabled="actionLoadingId === selectedRow?.id" @click="submitIssue">
            <span v-if="actionLoadingId === selectedRow?.id" class="spinner-border spinner-border-sm me-1"></span>
            Confirm Issue
          </button>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import moment from "moment";
import reportsService from "@/services/reports.service";
import { toast } from "@/utils/toast";

const route = useRoute();

const title = "Check Payroll Report";
const text = "Administration";
const text1 = "Check Payroll Report";

const loading = ref(false);
const actionLoadingId = ref(null);
const summary = ref(null);
const rows = ref([]);
const issueModalOpen = ref(false);
const selectedRow = ref(null);

const filters = reactive({
  frequency: "monthly",
  period: moment().format("YYYY-MM"),
  period_start: moment().startOf("week").format("YYYY-MM-DD"),
  period_end: moment().startOf("week").add(6, "days").format("YYYY-MM-DD"),
  status: "",
});

const issueForm = reactive({
  check_number: "",
  check_date: moment().format("YYYY-MM-DD"),
  receiver_name: "",
  receiver_document_no: "",
  notes: "",
});

const columns = [
  { title: "Emp ID", dataIndex: "employee_id", key: "employee_id" },
  { title: "Name", dataIndex: "employee_name", key: "employee_name" },
  { title: "Department", dataIndex: "department_name", key: "department_name" },
  { title: "Designation", dataIndex: "designation_name", key: "designation_name" },
  { title: "Amount", dataIndex: "amount", key: "amount" },
  { title: "Status", dataIndex: "status", key: "status" },
  { title: "Approval", dataIndex: "approval_status", key: "approval_status" },
  { title: "Check No.", dataIndex: "check_number", key: "check_number" },
  { title: "Check Date", dataIndex: "check_date", key: "check_date" },
  { title: "Issued At", dataIndex: "issued_at", key: "issued_at" },
  { title: "Issued By", dataIndex: "issued_by_name", key: "issued_by_name" },
  { title: "Actions", key: "actions" },
];

const cards = computed(() => {
  if (!summary.value) return [];
  return [
    { title: "Employees", value: summary.value.employee_count || 0, cls: "primary", icon: "ti ti-users" },
    { title: "Total Checks", value: summary.value.total_amount || 0, cls: "info", icon: "ti ti-file-invoice", isMoney: true },
    { title: "Issued", value: summary.value.issued_amount || 0, cls: "success", icon: "ti ti-checks", isMoney: true },
    { title: "Pending", value: summary.value.pending_amount || 0, cls: "warning", icon: "ti ti-alert-circle", isMoney: true },
  ];
});

const operatorCards = computed(() => {
  const readyRows = rows.value.filter((row) => row.status !== "issued" && row.approval_status === "approved");
  const blockedRows = rows.value.filter((row) => row.status !== "issued" && row.approval_status !== "approved");

  return [
    {
      title: "Ready To Issue",
      value: readyRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      cls: "success",
      icon: "ti ti-writing",
      isMoney: true,
      note: `${readyRows.length} employee(s) can be issued a check now`,
    },
    {
      title: "Blocked",
      value: blockedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      cls: "danger",
      icon: "ti ti-lock",
      isMoney: true,
      note: `${blockedRows.length} employee(s) await payroll approval`,
    },
  ];
});

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function resetIssueForm() {
  issueForm.check_number = "";
  issueForm.check_date = moment().format("YYYY-MM-DD");
  issueForm.receiver_name = "";
  issueForm.receiver_document_no = "";
  issueForm.notes = "";
}

function openIssue(record) {
  selectedRow.value = record;
  resetIssueForm();
  issueForm.receiver_name = record.receiver_name || record.employee_name || "";
  issueForm.receiver_document_no = record.receiver_document_no || "";
  issueForm.notes = record.notes || "";
  issueModalOpen.value = true;
}

function closeIssue() {
  issueModalOpen.value = false;
  selectedRow.value = null;
  resetIssueForm();
}

async function fetchReport() {
  loading.value = true;
  try {
    const params = buildParams();
    if (!params) {
      loading.value = false;
      return;
    }
    const { data } = await reportsService.checkPayroll({
      ...params,
      status: filters.status || undefined,
    });
    summary.value = data.summary || null;
    rows.value = data.rows || [];
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to load check payroll report.", "error");
  } finally {
    loading.value = false;
  }
}

async function exportReport(format) {
  try {
    const params = buildParams();
    if (!params) return;
    const res =
      format === "pdf"
        ? await reportsService.exportCheckPayrollPDF({
            ...params,
            status: filters.status || undefined,
          })
        : await reportsService.exportCheckPayrollCSV({
            ...params,
            status: filters.status || undefined,
          });

    downloadBlob(res.data, `check-payroll-${reportPeriodToken()}.${format}`);
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to export check payroll report.", "error");
  }
}

function buildParams() {
  if (filters.frequency === "monthly") {
    if (!filters.period) return null;
    return {
      frequency: "monthly",
      period: filters.period,
    };
  }

  if (!filters.period_start || !filters.period_end) return null;
  return {
    frequency: filters.frequency,
    period_start: filters.period_start,
    period_end: filters.period_end,
  };
}

function reportPeriodToken() {
  return filters.frequency === "monthly"
    ? filters.period
    : [filters.frequency, filters.period_start, filters.period_end].filter(Boolean).join("-");
}

async function submitIssue() {
  if (!selectedRow.value) return;
  if (!issueForm.check_number) {
    toast("Check number is required.", "error");
    return;
  }

  actionLoadingId.value = selectedRow.value.id;
  try {
    const { data } = await reportsService.issueCheckPayroll(selectedRow.value.id, {
      check_number: issueForm.check_number,
      check_date: issueForm.check_date || undefined,
      receiver_name: issueForm.receiver_name || undefined,
      receiver_document_no: issueForm.receiver_document_no || undefined,
      notes: issueForm.notes || undefined,
    });

    if (data?.duplicate_check_number_warning) {
      toast(data.duplicate_check_number_warning, "warning");
    } else {
      toast("Check issued.");
    }
    closeIssue();
    await fetchReport();
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to issue check.", "error");
  } finally {
    actionLoadingId.value = null;
  }
}

async function markPending(record) {
  actionLoadingId.value = record.id;
  try {
    await reportsService.markCheckPayrollPending(record.id);
    toast("Check disbursement reverted to pending.");
    await fetchReport();
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to revert check disbursement.", "error");
  } finally {
    actionLoadingId.value = null;
  }
}

function applyRouteFilters() {
  const q = route.query || {};
  if (q.frequency) {
    filters.frequency = String(q.frequency);
  }
  if (q.period) {
    filters.period = String(q.period);
  }
  if (q.period_start) {
    filters.period_start = String(q.period_start);
  }
  if (q.period_end) {
    filters.period_end = String(q.period_end);
  }
}

onMounted(() => {
  applyRouteFilters();
  fetchReport();
});
</script>

<style scoped>
.dq-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.dq-modal-card {
  width: min(720px, 100%);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.dq-modal-head,
.dq-modal-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.dq-modal-foot {
  border-bottom: 0;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
}

.dq-modal-body {
  padding: 1.25rem;
}
</style>
